import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/radio-group";
import { Switch } from "../../../../components/ui/switch";
import { Separator } from "../../../../components/ui/separator";
import {
  Settings,
  Search,
  ArrowLeft,
  Mail,
  Shield,
  Server,
  Key,
  Plus,
  Trash2,
  Check,
  Copy,
  AlertTriangle,
  Loader2
} from "lucide-react";
import { Link } from "wouter";
import { useToast } from "../../../../hooks/use-toast";
import { ApiToken, SettingProps } from "../interface";
import { myLocalStorage } from "../../../StorageHelper";
import { fetchEmails, getGenerationAdminConfig, postEmails, saveConfigureAdminSettings, updateSsoGeneralSetting } from "../Api/settingsApi";
import useUserStore from "../../../../services/userStore";
import { Badge } from "../../../ui/badge";
import { formatDate } from "../../formatTimeAgo";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../../ui/alert-dialog";
import { createToken, deleteToken, fetchTokens } from "../../TenantDetails/Api/tenantDetailsapi";




export default function GeneralSettings({ onBack }: SettingProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();
  const tenant = myLocalStorage.getItem("latestTenant");
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
  const [apiTokens, setApiTokens] = useState<ApiToken[]>([]);
  const [showCreateTokenDialog, setShowCreateTokenDialog] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [tokenToDelete, setTokenToDelete] = useState<string | null>(null);
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  const [selectedTenant, setSelectedTenant] = useState(tenant);
  const [adminAccess, setAdminAccess] = useState("ALL_MACHINES");
  const [isSsoEnabled, setIsSsoEnabled] = useState(false);
  const [isSsoLoading, setIsSsoLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingToken, setIsCreatingToken] = useState(false);
  const userEmail = useUserStore((state) => state.user.email);
  const [emailData, setEmailData] = useState({
    emailToAddress: "",
    emailToDisplay: "",
    isEnabled: false,
  });

  const filteredSections = useMemo(() => {
    if (!searchTerm) return ["endpoint-access", "email-notifications", "organization", "security", "api-tokens"];

    const searchLower = searchTerm.toLowerCase();
    const sections = [];

    if ("endpoint access connect administrator".includes(searchLower)) {
      sections.push("endpoint-access");
    }
    if ("email notifications admin approval".includes(searchLower)) {
      sections.push("email-notifications");
    }
    if ("organization company timezone language".includes(searchLower)) {
      sections.push("organization");
    }
    if ("security timeout password mfa authentication sso single sign on".includes(searchLower)) {
      sections.push("security");
    }
    if ("api token access audit logs third party integration".includes(searchLower)) {
      sections.push("api-tokens");
    }

    return sections;
  }, [searchTerm]);



  const maskToken = (token: string) => {
    return `${token.substring(0, 12)}${'•'.repeat(40)}${token.substring(token.length - 4)}`;
  };




  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast({
        title: "Error",
        description: "Please provide a name for the token.",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingToken(true);
    try {
      // Wait for token generation
      const generatedToken = await generateToken();

      if (!generatedToken) {
        toast({
          title: "Error",
          description: "Failed to generate token. Please try again.",
          variant: "destructive",
        });
        return;
      }

      const newToken: ApiToken = {
        id: `token-${Date.now()}`,
        name: newTokenName,
        token: generatedToken,
        createdAt: new Date().toISOString(),
        lastUsed: null,
      };

      setApiTokens((prev) => [...prev, newToken]);
      setGeneratedToken(generatedToken);
      setNewTokenName("");

      toast({
        title: "Token Created",
        description: "Your API access token has been generated successfully.",
      });
    } catch (error) {
      console.error("Error creating token:", error);
      toast({
        title: "Error",
        description: "Something went wrong while creating the token.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingToken(false);
    }
  };

  const generateToken = async (): Promise<string | null> => {
    try {
      const createResponse = await createToken(tenantName, userEmail);
      if (!createResponse) return null;

      const fetchResponse = await fetchTokens(tenantName, userEmail);
      if (fetchResponse && fetchResponse.apiKey) {
        return fetchResponse.apiKey;
      }

      return null;
    } catch (error) {
      console.error("Error generating token:", error);
      return null;
    }
  };


  const handleDeleteToken = async (tokenId: string) => {
    await deleteToken(tenant, userEmail, tokenId)
    setApiTokens(prev => prev.filter(t => t.token !== tokenId));
    setTokenToDelete(null);
    toast({
      title: "Token Deleted",
      description: "The API token has been permanently deleted.",
    });
  };

  const copyToClipboard = (text: string, tokenId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedTokenId(tokenId);
    setTimeout(() => setCopiedTokenId(null), 2000);
    toast({
      title: "Copied",
      description: "Token copied to clipboard.",
    });
  };


  const handleEndpointAccessChange = async (value: string) => {
    setAdminAccess(value);
    try {
      await saveConfigureAdminSettings(selectedTenant?.tenantName, value)
    } catch (err) {
      console.error(err);
    }

  };


  const handleChange = (key: string, value: string) => {

    setEmailData({ ...emailData, [key]: value });
  };


  const getAdminConfig = async (name: string) => {
    try {
      let configValue: any = await getGenerationAdminConfig(selectedTenant?.tenantName, name)

      if (configValue) {
        if (configValue.settingsValue) {
          setAdminAccess(configValue.settingsValue);
        } else {
          setAdminAccess("ALL_MACHINES");
        }
      } else {
        setAdminAccess("ALL_MACHINES");
      }
    } catch (err) {
      console.log(err);
      setAdminAccess("ALL_MACHINES");
    }
  };

  const getSsoConfig = async () => {
    try {
      const configValue: any = await getGenerationAdminConfig(selectedTenant?.tenantName, "IS_SSO_ENABLED");
      setIsSsoEnabled(Boolean(configValue?.settingsValue));
    } catch (err) {
      console.log(err);
      setIsSsoEnabled(false);
    }
  };

  useEffect(() => {
    getAdminConfig("endpointConfig");
    getSsoConfig();
  }, [selectedTenant]);


  const handleSsoToggle = async (checked: boolean) => {
    if (!selectedTenant?.tenantName) {
      return;
    }

    setIsSsoLoading(true);

    try {
      await updateSsoGeneralSetting(selectedTenant.tenantName, checked);
      setIsSsoEnabled(checked);
      toast({
        title: "SSO Updated",
        description: checked
          ? "SSO has been enabled for this tenant."
          : "SSO has been disabled for this tenant.",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Unable to update SSO setting.",
        variant: "destructive",
      });
    } finally {
      setIsSsoLoading(false);
    }
  };


  const toggleNotifications = async () => {
    setEmailData({ ...emailData, isEnabled: !emailData.isEnabled });

    const { isEnabled } = await postEmails({
      tenantName: tenantName,
      emailToAddress: emailData.emailToAddress,
      emailToDisplay: emailData.emailToDisplay,
      isEnabled: !emailData.isEnabled,
      emailOfAdmin: userEmail,
    });

    setEmailData({ ...emailData, isEnabled });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (emailData.isEnabled) {
      if (!emailData.emailToDisplay?.trim()) {
        toast({
          title: "Validation Error",
          variant: "destructive",
          description: "Display name is required when email is enabled.",
        });
        return;
      }

      if (!emailData.emailToAddress?.trim()) {
        toast({
          title: "Validation Error",
          variant: "destructive",
          description: "Email address is required when email is enabled.",
        });
        return;
      }
    }
    setIsSaving(true);
    try {
      const emails = await postEmails({
        tenantName: tenantName,
        emailToAddress: emailData.emailToAddress,
        emailToDisplay: emailData.emailToDisplay,
        isEnabled: emailData.isEnabled,
        emailOfAdmin: userEmail,
      });

      if (emails?.length)
        setEmailData({
          isEnabled: emails[0].isEnabled || false,
          emailToAddress: emails[0].emailToAddress || "",
          emailToDisplay: emails[0].emailToDisplay || "",
        });
      toast({
        title: "Settings Saved",
        description: "General settings have been updated successfully.",
      });

    } catch (error) {
      toast({
        title: "Error Saving Settings",
        variant: "destructive",
        description: "Error occuring during saving.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getTokens = async () => {
    try {
      const fetchResponse = await fetchTokens(tenantName, userEmail);
      if (fetchResponse && fetchResponse.apiKey) {
        const newToken: ApiToken = {
          id: `token-${Date.now()}`,
          name: "",
          token: fetchResponse.apiKey,
          createdAt: new Date().toISOString(),
          lastUsed: null,
        };
        setApiTokens((prev) => [...prev, newToken]);
      }
    } catch (e) {

    }

  }

  useEffect(() => {
    const getEmails = async () => {
      const emails = await fetchEmails(tenantName);

      if (emails?.length && emails[emails.length - 1]?.emailToAddress) {
        setEmailData({
          isEnabled: emails[emails.length - 1].isEnabled || false,
          emailToAddress: emails[emails.length - 1].emailToAddress || "",
          emailToDisplay: emails[emails.length - 1].emailToDisplay || "",
        });
      }
    };

    getEmails();
    getTokens();
  }, [tenantName]);





  return (
    <div className="tenant-settings-page flex-1 space-y-6 p-6">
      {/* Header */}
      <Card className="settings-header-card border-border/40 rounded-2xl">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="settings-back-button"
                onClick={onBack}
                aria-label="Back to settings"
                title="Back to settings"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                  General Settings
                </h1>
                <p className="text-muted-foreground">
                  Configure basic tenant and organization settings
                </p>
              </div>
            </div>
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search settings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="input-search-settings"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings Content */}
      <div className="space-y-6">
        {/* Endpoint Access Settings */}
        {filteredSections.includes("endpoint-access") && (
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Allow administrator to connect the endpoints
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={adminAccess}
                onValueChange={handleEndpointAccessChange}
                className="space-y-3"
              >
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="ALL_MACHINES" id="access-all" />
                  <Label htmlFor="access-all" className="font-normal">
                    Access to All machines
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="SERVER_ONLY" id="access-server" />
                  <Label htmlFor="access-server" className="font-normal">
                    Only server machines
                  </Label>
                </div>
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="NO_ACCESS" id="access-none" />
                  <Label htmlFor="access-none" className="font-normal">
                    No Access
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        )}

        {/* Email Notifications */}
        {filteredSections.includes("email-notifications") && (
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Email Notifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">
                    Admin email notifications for resource request approvals.
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {emailData?.isEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={emailData?.isEnabled}
                    onClick={() => toggleNotifications()}

                    className="data-[state=checked]:bg-blue-600"
                    data-testid="switch-email-notifications"
                  />
                </div>
              </div>

              {emailData?.isEnabled && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email-address" className="text-sm text-muted-foreground">
                      Email address
                    </Label>
                    <Input
                      id="emailToAddress"
                      value={emailData.emailToAddress}
                      onChange={(e) => handleChange("emailToAddress", e.target.value)}
                      placeholder="admin@company.com"
                      data-testid="input-admin-email"
                      name="emailToAddress"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="display-name" className="text-sm text-muted-foreground">
                      Display name
                    </Label>
                    <Input
                      id="display-name"
                      value={emailData.emailToDisplay}
                      onChange={(e) => handleChange("emailToDisplay", e.target.value)}
                      name="emailToDisplay"
                      placeholder="Admin Name"
                      data-testid="input-display-name"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {filteredSections.includes("organization") && (
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Organization Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Tenant Name</Label>
                  <Input
                    id="org-name"
                    value={tenantName}
                    disabled
                    data-testid="input-org-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    value={Intl.DateTimeFormat().resolvedOptions().timeZone}
                    disabled
                    data-testid="input-timezone"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Security Settings */}
        {filteredSections.includes("security") && (
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/*
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">SSO</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable or disable tenant SSO.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    {isSsoEnabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={isSsoEnabled}
                    disabled={isSsoLoading}
                    onCheckedChange={handleSsoToggle}
                    className="data-[state=checked]:bg-blue-600"
                    data-testid="switch-sso-setting"
                  />
                </div>
              </div>
              <Separator />
              */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Strong Password Policy</Label>
                  <p className="text-sm text-muted-foreground">
                    Require complex passwords with minimum 8 characters
                  </p>
                </div>
                <Switch
                  checked
                  disabled

                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Multi-Factor Authentication</Label>
                  <p className="text-sm text-muted-foreground">
                    Require MFA for users not using Passkey authentication
                  </p>
                </div>
                <Switch
                  checked
                  disabled
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
            </CardContent>
          </Card>
        )}


        {/* API Access Tokens */}
        {filteredSections.includes("api-tokens") && (
          <Card className="border-border/40 rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    API Access Tokens
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage API tokens for third-party audit log integrations
                  </p>
                </div>
                <Button
                  onClick={() => setShowCreateTokenDialog(true)}
                  className="gap-2"
                  data-testid="button-create-token"
                  disabled={isCreatingToken}
                >
                  <Plus className="w-4 h-4" />
                  Create Token
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {apiTokens.length === 0 ? (
                <div className="settings-dropzone text-center py-12 border-2 border-dashed rounded-lg">
                  <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No API tokens</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first API token to enable third-party integrations
                  </p>
                  <Button
                    onClick={() => setShowCreateTokenDialog(true)}
                    className="gap-2"
                    data-testid="button-create-first-token"
                    disabled={isCreatingToken}
                  >
                    <Plus className="w-4 h-4" />
                    Create Token
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {apiTokens.map((token) => (
                    <Card key={token.id} className="border bg-card/50 rounded-2xl">
                      <CardContent className="pt-4">
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-sm">{token.name}</h4>
                                {!token.lastUsed && (
                                  <Badge variant="secondary" className="text-xs">
                                    Never Used
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>Created: {formatDate(token.createdAt)}</span>
                                {token.lastUsed && (
                                  <>
                                    <span>•</span>
                                    <span>Last used: {formatDate(token.lastUsed)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setTokenToDelete(token.token)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`button-delete-token-${token.token}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-muted/50 rounded-md px-3 py-2 font-mono text-xs break-all">
                              {maskToken(token.token)}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyToClipboard(token.token, token.id)}
                              className="gap-2 flex-shrink-0"
                              data-testid={`button-copy-token-${token.id}`}
                            >
                              {copiedTokenId === token.id ? (
                                <>
                                  <Check className="w-4 h-4" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4" />
                                  Copy
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}


        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            className="gap-2"
            data-testid="button-save-settings"
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Settings className="w-4 h-4" />
            )}
            {isSaving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        {/* Empty State */}
        {filteredSections.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No settings found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms or browse all available settings.
            </p>
          </div>
        )}
      </div>



      <Dialog open={showCreateTokenDialog} onOpenChange={(open) => {
        setShowCreateTokenDialog(open);
        if (!open) {
          setGeneratedToken(null);
          setNewTokenName("");
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              {generatedToken ? "Token Created" : "Create API Token"}
            </DialogTitle>
            <DialogDescription>
              {generatedToken
                ? "Save this token now. You won't be able to see it again!"
                : "Create a new API token for third-party audit log integrations"
              }
            </DialogDescription>
          </DialogHeader>

          {generatedToken ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-900 dark:text-amber-100">
                      Important: Save this token securely
                    </p>
                    <p className="text-amber-700 dark:text-amber-300 mt-1">
                      This is the only time you'll see the full token. Store it in a secure location.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Your API Token</Label>
                <div className="flex gap-2">
                  <div className="flex-1 bg-muted rounded-md px-3 py-2 font-mono text-sm break-all border-2 border-blue-200 dark:border-blue-800">
                    {generatedToken}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(generatedToken, 'new-token')}
                    className="gap-2 flex-shrink-0"
                    data-testid="button-copy-new-token"
                  >
                    {copiedTokenId === 'new-token' ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  onClick={() => {
                    setShowCreateTokenDialog(false);
                    setGeneratedToken(null);
                  }}
                  data-testid="button-close-token-dialog"
                >
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="token-name" className="text-sm font-medium">
                  Token Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="token-name"
                  placeholder="e.g., Production Audit Log Integration"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  data-testid="input-token-name"
                />
                <p className="text-xs text-muted-foreground">
                  A descriptive name to help you identify this token
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateTokenDialog(false);
                    setNewTokenName("");
                  }}
                  data-testid="button-cancel-create-token"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateToken}
                  disabled={!newTokenName.trim()}
                  className="gap-2"
                  data-testid="button-generate-token"
                >
                  <Key className="w-4 h-4" />
                  Generate Token
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Token Confirmation Dialog */}
      <AlertDialog open={!!tokenToDelete} onOpenChange={(open) => !open && setTokenToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete API Token
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this API token? This action cannot be undone and will immediately revoke access for any integrations using this token.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-token">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => tokenToDelete && handleDeleteToken(tokenToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-token"
            >
              Delete Token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
